import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Mail, Lock, User, ArrowRight, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { authAPI } from '../lib/api';
import { toast } from 'sonner';
import { useTheme } from '../App';

export default function AuthPage({ onLogin }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', name: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        const res = await authAPI.login({ email: form.email, password: form.password });
        localStorage.setItem('token', res.data.token);
        localStorage.setItem('user', JSON.stringify(res.data.user));
        onLogin(res.data.user);
        toast.success('Welcome back!');
        
        // Redirect based on role
        if (res.data.user.role === 'super_admin') navigate('/admin');
        else if (res.data.user.role === 'authority') navigate('/authority');
        else navigate('/dashboard');
      } else {
        const res = await authAPI.register({ email: form.email, password: form.password, name: form.name });
        localStorage.setItem('token', res.data.token);
        localStorage.setItem('user', JSON.stringify(res.data.user));
        onLogin(res.data.user);
        toast.success('Account created!');
        navigate('/dashboard');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen flex items-center justify-center px-6 relative transition-colors ${isDark ? '' : 'bg-[#F8FAFC]'}`} data-testid="auth-page">
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{ background: isDark ? 'radial-gradient(circle, rgba(59,130,246,0.08) 0%, transparent 70%)' : 'radial-gradient(circle, rgba(59,130,246,0.04) 0%, transparent 70%)' }} />

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md relative">
        <div className="text-center mb-8">
          <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
            className={`inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-4 ${isDark ? 'bg-blue-500/10 border border-blue-500/20' : 'bg-blue-50 border border-blue-200'}`}>
            <Shield className="w-7 h-7 text-blue-500" />
          </motion.div>
          <h1 className={`text-3xl font-bold tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`} style={{ fontFamily: 'Outfit' }}>
            {isLogin ? t('auth.loginTitle') : t('auth.registerTitle')}
          </h1>
          <p className={`mt-2 text-sm ${isDark ? 'text-white/40' : 'text-gray-400'}`}>myAct Platform</p>
        </div>

        <div className={`backdrop-blur-xl rounded-2xl p-8 border ${isDark ? 'bg-[#0A0A0C]/60 border-white/[0.06]' : 'bg-white border-gray-200 shadow-xl shadow-gray-200/50'}`}>
          <form onSubmit={handleSubmit} className="space-y-5">
            {!isLogin && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}>
                <Label className={`text-sm mb-2 block ${isDark ? 'text-white/60' : 'text-gray-500'}`}>{t('auth.name')}</Label>
                <div className="relative">
                  <User className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-white/20' : 'text-gray-300'}`} />
                  <Input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className={`pl-10 h-12 rounded-xl ${isDark ? 'bg-white/[0.03] border-white/[0.06] text-white' : 'bg-gray-50 border-gray-200 text-gray-900'} focus:border-blue-500/40`}
                    placeholder="John Doe" data-testid="register-name-input" />
                </div>
              </motion.div>
            )}
            <div>
              <Label className={`text-sm mb-2 block ${isDark ? 'text-white/60' : 'text-gray-500'}`}>{t('auth.email')}</Label>
              <div className="relative">
                <Mail className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-white/20' : 'text-gray-300'}`} />
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className={`pl-10 h-12 rounded-xl ${isDark ? 'bg-white/[0.03] border-white/[0.06] text-white' : 'bg-gray-50 border-gray-200 text-gray-900'} focus:border-blue-500/40`}
                  placeholder="you@example.com" required data-testid="auth-email-input" />
              </div>
            </div>
            <div>
              <Label className={`text-sm mb-2 block ${isDark ? 'text-white/60' : 'text-gray-500'}`}>{t('auth.password')}</Label>
              <div className="relative">
                <Lock className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-white/20' : 'text-gray-300'}`} />
                <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className={`pl-10 h-12 rounded-xl ${isDark ? 'bg-white/[0.03] border-white/[0.06] text-white' : 'bg-gray-50 border-gray-200 text-gray-900'} focus:border-blue-500/40`}
                  placeholder="••••••••" required data-testid="auth-password-input" />
              </div>
            </div>
            <Button type="submit" disabled={loading} className="w-full h-12 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-medium text-base" data-testid="auth-submit-btn">
              {loading ? t('common.loading') : (isLogin ? t('auth.loginBtn') : t('auth.registerBtn'))}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </form>
          <div className="mt-6 text-center">
            <button onClick={() => setIsLogin(!isLogin)} className="text-sm text-blue-500 hover:text-blue-400 transition-colors" data-testid="auth-toggle-btn">
              {isLogin ? t('auth.switchToRegister') : t('auth.switchToLogin')}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
